import { createApiClient, type ApiClientConfig, type ApiPaths, type InitParam, type Page } from '@hello-aria/api-client'
import type { MaybeOptionalInit } from 'openapi-fetch'
import type { PathsWithMethod } from 'openapi-typescript-helpers'
import { toPage } from './request'
import type { ListItemOf, ListPathOf, MakaluPaths } from './types'

export type MakaluClientConfig = ApiClientConfig

/**
 * A makalu-api client whose methods resolve the response body and throw an `HttpError` on failure.
 * An app that calls routes the reference leaves out passes the generated paths with its own added.
 */
export const createMakaluClient = <Paths extends ApiPaths = MakaluPaths>(config: MakaluClientConfig) => {
    const { methods } = createApiClient<Paths>(config)

    return {
        ...methods,
        // makalu-api puts the pagination of a list in headers, which `GET` does not return.
        listPage: async <
            Path extends ListPathOf<Paths> & PathsWithMethod<Paths, 'get'>,
            Init extends MaybeOptionalInit<Paths[Path], 'get'>
        >(
            url: Path,
            ...init: InitParam<Init>
        ): Promise<Page<ListItemOf<Paths, Path>>> => {
            const { data, response } = await methods.GETWithResponse<Path, Init>(url, ...init)
            // An answer with no content is an empty list, as `requestService` read it.
            if (data === undefined) return toPage([], response.headers)
            if (!Array.isArray(data)) throw new Error(`${String(url)} did not answer a list`)

            return toPage(data, response.headers)
        }
    }
}

export type MakaluClient<Paths extends ApiPaths = MakaluPaths> = ReturnType<typeof createMakaluClient<Paths>>
